import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { GanttChartSquare, Loader2 } from "lucide-react";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [role, setRole] = useState("employee");

  const doLogin = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}!`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register({ name, email: regEmail, password: regPassword, role });
      toast.success(`Account created. Welcome, ${u.name.split(" ")[0]}!`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 relative overflow-hidden flex-col justify-between p-12">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #2563EB 0, transparent 40%), radial-gradient(circle at 80% 80%, #10B981 0, transparent 40%)" }} />
        <div className="relative flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
            <GanttChartSquare className="h-6 w-6 text-white" />
          </div>
          <span className="font-heading font-bold text-white text-xl">NexusLMS</span>
        </div>
        <div className="relative">
          <h1 className="font-heading text-4xl font-bold text-white leading-tight tracking-tight">
            Enterprise training,<br />compliance &amp;<br />certification — unified.
          </h1>
          <p className="text-slate-400 mt-5 text-base max-w-md leading-relaxed">
            Schedule sessions, track mandatory training, issue verifiable certificates, and stay audit-ready across every department.
          </p>
          <div className="flex gap-8 mt-10">
            <div><p className="font-heading text-3xl font-bold text-white">30/60/90</p><p className="text-sm text-slate-400 mt-1">Day expiry horizon</p></div>
            <div><p className="font-heading text-3xl font-bold text-white">3 Roles</p><p className="text-sm text-slate-400 mt-1">Admin · Trainer · Learner</p></div>
          </div>
        </div>
        <p className="relative text-xs text-slate-500">© 2026 NexusLMS. Built for enterprise L&amp;D teams.</p>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md stagger-in">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
              <GanttChartSquare className="h-6 w-6 text-white" />
            </div>
            <span className="font-heading font-bold text-xl">NexusLMS</span>
          </div>

          <Tabs defaultValue="login">
            <TabsList className="grid grid-cols-2 w-full mb-6">
              <TabsTrigger value="login" data-testid="login-tab">Sign in</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Create account</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <h2 className="font-heading text-2xl font-bold tracking-tight">Welcome back</h2>
              <p className="text-sm text-muted-foreground mt-1 mb-6">Sign in to access your training dashboard.</p>
              <form onSubmit={doLogin} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                    placeholder="you@company.com" className="mt-1.5" data-testid="login-email-input" />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
                  </div>
                  <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                    placeholder="••••••••" className="mt-1.5" data-testid="login-password-input" />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <h2 className="font-heading text-2xl font-bold tracking-tight">Create your account</h2>
              <p className="text-sm text-muted-foreground mt-1 mb-6">Join your organization's training platform.</p>
              <form onSubmit={doRegister} className="space-y-4">
                <div>
                  <Label htmlFor="rname">Full name</Label>
                  <Input id="rname" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Jane Doe" className="mt-1.5" data-testid="register-name-input" />
                </div>
                <div>
                  <Label htmlFor="remail">Email</Label>
                  <Input id="remail" type="email" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} required placeholder="you@company.com" className="mt-1.5" data-testid="register-email-input" />
                </div>
                <div>
                  <Label htmlFor="rpassword">Password</Label>
                  <Input id="rpassword" type="password" value={regPassword} onChange={(e) => setRegPassword(e.target.value)} required placeholder="Create a password" className="mt-1.5" data-testid="register-password-input" />
                </div>
                <div>
                  <Label>I am joining as a</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1.5">
                    {["employee", "trainer"].map((r) => (
                      <button type="button" key={r} onClick={() => setRole(r)}
                        data-testid={`register-role-${r}`}
                        className={`p-2.5 rounded-lg border text-sm capitalize transition-colors ${role === r ? "border-primary bg-accent/50 text-primary font-medium" : "border-border"}`}>
                        {r === "employee" ? "learner" : r}
                      </button>
                    ))}
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-button">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Looking to buy a course? <Link to="/shop" className="text-primary hover:underline font-medium" data-testid="goto-shop-link">Visit the course shop</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
